import { Menu, Globe2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { DynamicIcon } from "@/components/shared/IconMap";
import { PRACTICE_AREAS_NAV } from "@/constants/firmInfo";
import { PRACTICE_AREAS_ES } from "@/i18n/translations";

/**
 * Slide-out mobile navigation sheet. Extracted from Header.jsx so the
 * (fairly large) mobile menu markup doesn't inflate the main Header
 * component's size/complexity.
 */
export const MobileNav = ({
  open,
  onOpenChange,
  isSpanish,
  toggleLanguage,
  t,
  navKeyByPath,
  secondaryNavLinks,
}) => {
  const practiceAreaTitle = (area) => (isSpanish ? PRACTICE_AREAS_ES[area.slug]?.title || area.title : area.title);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <button className="p-2 text-white" data-testid="header-mobile-menu-button" aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-white w-[85%] sm:max-w-sm">
        <nav className="flex flex-col gap-1 mt-8">
          <button
            type="button"
            onClick={toggleLanguage}
            className="self-start flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border border-slate-200 mb-4 outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--gold-500)]"
            data-testid="mobile-language-toggle-button"
            aria-label={isSpanish ? "Switch to English" : "Cambiar a Español"}
          >
            <Globe2 className="h-4 w-4 text-[color:var(--gold-500)]" />
            <span className={!isSpanish ? "text-[color:var(--navy-950)]" : "text-slate-400"}>EN</span>
            <span className="text-slate-300">/</span>
            <span className={isSpanish ? "text-[color:var(--navy-950)]" : "text-slate-400"}>ES</span>
          </button>
          <SheetClose asChild>
            <Link to="/" className="py-3 text-base font-medium text-[color:var(--navy-950)] border-b border-slate-100" data-testid="mobile-nav-link-home">
              {t("nav.home")}
            </Link>
          </SheetClose>
          <p className="pt-4 pb-1 text-xs uppercase tracking-[0.18em] text-slate-500">{t("nav.practiceAreas")}</p>
          {PRACTICE_AREAS_NAV.map((area) => (
            <SheetClose asChild key={area.slug}>
              <Link
                to={`/practice-areas/${area.slug}`}
                className="flex items-center gap-3 py-2.5 text-sm text-[color:var(--navy-950)]"
                data-testid={`mobile-nav-practice-area-${area.slug}`}
              >
                <DynamicIcon name={area.icon} className="h-4 w-4 text-[color:var(--gold-500)]" />
                {practiceAreaTitle(area)}
              </Link>
            </SheetClose>
          ))}
          <div className="border-t border-slate-100 mt-3 pt-3 flex flex-col gap-1">
            {secondaryNavLinks.map((link) => (
              <SheetClose asChild key={link.to}>
                <Link to={link.to} className="py-2.5 text-base font-medium text-[color:var(--navy-950)]" data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {t(navKeyByPath[link.to] || "nav.home", link.label)}
                </Link>
              </SheetClose>
            ))}
          </div>
          <SheetClose asChild>
            <Link to="/contact" className="mt-4">
              <Button className="w-full bg-[color:var(--gold-500)] text-[color:var(--navy-950)] hover:bg-[color:var(--gold-400)] font-semibold rounded-full h-11" data-testid="mobile-schedule-consultation-link">
                {t("nav.schedule")}
              </Button>
            </Link>
          </SheetClose>
        </nav>
      </SheetContent>
    </Sheet>
  );
};
